import { notifyAboutDisposedRenderer } from "libs/UpdatableTexture";
import { document, location } from "libs/window";
import { WebGLRenderer } from "three";
import { Store } from "viewer/singletons";
import type { WebknossosState } from "viewer/store";

let renderer: WebGLRenderer | null = null;

// True while the current webglcontextlost was caused by destroyRenderer() (our own
// teardown) rather than a real GPU crash. Set when we force the loss, cleared once a
// new renderer (fresh context) is created — so a later genuine loss is reported normally.
let wasContextLossForcedByTeardown = false;

export function wasContextLossForced(): boolean {
  return wasContextLossForcedByTeardown;
}

export function destroyRenderer(): void {
  if (renderer == null) return;
  renderer.dispose();
  wasContextLossForcedByTeardown = true;
  renderer.forceContextLoss();
  renderer = null;
  notifyAboutDisposedRenderer();
}

export function getRenderer(): WebGLRenderer {
  if (renderer != null) return renderer;
  // A fresh context is being created, so any future context loss is genuine again.
  wasContextLossForcedByTeardown = false;

  const renderCanvasElement = document.getElementById("render-canvas");
  renderer = (
    renderCanvasElement != null
      ? // Create a WebGL2 renderer
        new WebGLRenderer({
          canvas: renderCanvasElement,
          // This prevents flickering when rendering to a buffer instead of the canvas
          preserveDrawingBuffer: true,
          // Apparently, there's a bug in the antialias implementation of browsers so that
          // varyings that are marked with a flat modifier are still being interpolated.
          // This caused 1-fragment-wide stripes in the rendering output. Debugging the shader code
          // showed that the bucket addresses which are passed from vertex to fragment shader
          // were interpolated sometimes. Disabling antialiasing helped a bit for that, but there
          // were still problems which is why the fragment shader doesn't use the flat varying
          // for texels close to the bucket borders. Consequently, antialiasing can be enabled
          // without problems (probably) apart from a potential performance drop.
          antialias: Store.getState().userConfiguration.antialiasRendering,
        })
      : {}
  ) as WebGLRenderer;

  return renderer;
}

type GpuInfo = { vendor: string | null; renderer: string | null };

// The GPU vendor never changes during a session. Caching it avoids creating a context just for
// analytics, which can be expensive (especially with software rendering) and blocks the main thread.
let cachedGpuInfo: GpuInfo | null = null;

export function readGpuInfo(gl: WebGLRenderingContext | WebGL2RenderingContext): GpuInfo {
  if (cachedGpuInfo != null) return cachedGpuInfo;
  const info: GpuInfo = { vendor: null, renderer: null };
  const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
  if (debugInfo != null) {
    info.vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
    info.renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
    cachedGpuInfo = info;
  }
  return info;
}

function getGpuInfo(): GpuInfo {
  if (cachedGpuInfo != null) return cachedGpuInfo;
  // Prefer the context that is already in use. Creating a new one is only a fallback.
  const activeContext = renderer?.getContext();
  if (activeContext != null && !activeContext.isContextLost()) {
    return readGpuInfo(activeContext);
  }
  const gl = document.createElement("canvas").getContext("webgl2");
  if (gl == null) return { vendor: null, renderer: null };
  const info = readGpuInfo(gl);
  gl.getExtension("WEBGL_lose_context")?.loseContext();
  return info;
}

export function getWebGlAnalyticsInformation(state: WebknossosState) {
  const interpolationEnabled = state.datasetConfiguration.interpolation;

  return {
    url: location.href,
    userAgent: navigator?.userAgent,
    platform: navigator?.platform,
    interpolationEnabled,
    ...getGpuInfo(),
  };
}

if (typeof window !== "undefined") {
  // Call window.testContextLoss() in the console
  // to test the context loss recovery.
  function testContextLoss() {
    const renderer = getRenderer();
    const ext = renderer.getContext().getExtension("WEBGL_lose_context");
    if (ext == null) {
      return;
    }
    ext.loseContext();
    setTimeout(() => ext.restoreContext(), 2500);
  }
  // @ts-expect-error
  window.testContextLoss = testContextLoss;
}
